import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import Tooltip from '@mui/material/Tooltip';

import dayjs from 'dayjs';

import {
  BreadcrumbHeader,
  FilterTabs,
  StatusBadge,
  EmptyState,
  ActionMenu,
  CreateRMAModal,
  DispositionModal } from
'../../components/shared';
import { rmas as rmasData } from '../../services/dataClient';
import '../../styles/design-tokens.css';
import { fieldSx } from '../../theme/fieldStyles';

// Break at the last space within `max` chars, or hard-cut if no space
import ContentCard from "../../components/shared/ContentCard";function truncate(text, max = 14) {
  if (!text || text.length <= max) return { short: text, truncated: false };
  const breakAt = text.lastIndexOf(' ', max);
  const short = (breakAt > 4 ? text.slice(0, breakAt) : text.slice(0, max)) + '…';
  return { short, truncated: true };
}

// ─── Constants ────────────────────────────────────────────────────────────────

const TABS = [
{ label: 'All', value: 'all' },
{ label: 'Pending', value: 'Pending' },
{ label: 'Received', value: 'Received' },
{ label: 'Inspecting', value: 'Inspecting' },
{ label: 'Restocked', value: 'Restocked' },
{ label: 'Returned to Supplier', value: 'Returned to Supplier' },
{ label: 'Scrapped', value: 'Scrapped' }];

const TH = {
  backgroundColor: '#eef2f6',
  fontWeight: 600,
  fontSize: '0.75rem',
  color: 'var(--grey-600)',
  textTransform: 'none',
  letterSpacing: 'normal',
  px: 2,
  py: '10px',
  borderBottom: '1px solid var(--grey-200)',
  whiteSpace: 'nowrap'
};

const TD = {
  px: 2,
  py: 0,
  border: 'none',
  fontSize: '0.875rem',
  color: 'var(--grey-800)',
  height: 48
};

// Resolved statuses (no further action needed)
const RESOLVED = new Set(['Restocked', 'Returned to Supplier', 'Scrapped']);

// ─── Stat card ────────────────────────────────────────────────────────────────
function StatCard({ label, value, accent }) {
  return (
    <ContentCard sx={{
      flex: 1,

      px: 3,
      py: 2
    }}>
      <Typography sx={{ fontSize: '1.75rem',
        fontWeight: 700,
        color: accent ?? 'var(--grey-900)',
        lineHeight: 1.1
      }}>
        {value}
      </Typography>
      <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-500)', mt: '4px' }}>
        {label}
      </Typography>
    </ContentCard>);

}

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function ReverseLogisticsPage() {
  const navigate = useNavigate();

  // ── State ──────────────────────────────────────────────────────────────────
  const [rmas, setRmas] = useState(rmasData);
  const [activeTab, setActiveTab] = useState('all');
  const [search, setSearch] = useState('');
  const [banner, setBanner] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [dispositionRma, setDispositionRma] = useState(null);

  const showBanner = (msg) => {
    setBanner(msg);
    setTimeout(() => setBanner(''), 4000);
  };

  // ── Derived data ────────────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const total = rmas.length;
    const pending = rmas.filter((r) => r.status === 'Pending').length;
    const inProgress = rmas.filter((r) => ['Received', 'Inspecting'].includes(r.status)).length;
    const resolved = rmas.filter((r) => RESOLVED.has(r.status)).length;
    return { total, pending, inProgress, resolved };
  }, [rmas]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rmas
    .filter((r) => activeTab === 'all' || r.status === activeTab)
    .filter((r) =>
    !q ||
    r.rmaNumber.toLowerCase().includes(q) ||
    r.ticketNumber.toLowerCase().includes(q) ||
    r.partNumber.toLowerCase().includes(q) ||
    r.customerName.toLowerCase().includes(q) ||
    r.returnReason.toLowerCase().includes(q)
    );
  }, [rmas, activeTab, search]);

  // ── Handlers ────────────────────────────────────────────────────────────────
  const handleMarkReceived = (rma) => {
    setRmas((prev) => prev.map((r) =>
    r.id === rma.id ?
    { ...r, status: 'Received', receivedDate: dayjs().format('YYYY-MM-DD') } :
    r
    ));
    showBanner(`${rma.rmaNumber} marked as Received.`);
  };

  const handleStartInspection = (rma) => {
    setRmas((prev) => prev.map((r) =>
    r.id === rma.id ? { ...r, status: 'Inspecting' } : r
    ));
    showBanner(`${rma.rmaNumber} moved to Inspecting.`);
  };

  const handleSaveDisposition = (result) => {
    if (!dispositionRma) return;
    setRmas((prev) => prev.map((r) =>
    r.id === dispositionRma.id ?
    {
      ...r,
      disposition: result.disposition,
      status: result.resultStatus,
      inspectionNotes: result.inspectionNotes,
      resolvedDate: RESOLVED.has(result.resultStatus) ? dayjs().format('YYYY-MM-DD') : null
    } :
    r
    ));
    showBanner(`${dispositionRma.rmaNumber} disposition recorded: ${result.disposition}.`);
    setDispositionRma(null);
  };

  const handleCreateRma = (newRma) => {
    rmasData.unshift(newRma); // persist in module-level array for detail page navigation
    setRmas((prev) => [newRma, ...prev]);
    showBanner(`${newRma.rmaNumber} created successfully.`);
    setCreateOpen(false);
  };

  // Build ActionMenu items per RMA status
  const getActions = (rma) => {
    const actions = [
    { label: 'View Details', onClick: () => navigate(`/inventory/reverse-logistics/${rma.id}`) }];

    if (rma.status === 'Pending') {
      actions.push({ label: 'Mark as Received', onClick: () => handleMarkReceived(rma) });
    }
    if (rma.status === 'Received') {
      actions.push({ label: 'Start Inspection', onClick: () => handleStartInspection(rma) });
    }
    if (rma.status === 'Inspecting') {
      actions.push({ label: 'Record Disposition', onClick: () => setDispositionRma(rma) });
    }
    return actions;
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ pt: 0, px: 3, pb: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>

      {/* Success banner */}
      {banner &&
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px',
        backgroundColor: '#E8F5E9', border: '1px solid #A5D6A7',
        borderRadius: '8px', px: 2, py: '12px'
      }}>
          <CheckCircleIcon sx={{ color: '#2E7D32', fontSize: 20 }} />
          <Typography sx={{ fontSize: '0.875rem', color: '#2E7D32', fontWeight: 600 }}>
            {banner}
          </Typography>
        </Box>
      }

      <BreadcrumbHeader
        title="Reverse Logistics"
        crumbs={[
        { label: 'Inventory' },
        { label: 'Reverse Logistics' }]
        } />

      {/* ── Stat cards ──────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        <StatCard label="Total RMAs" value={stats.total} />
        <StatCard label="Pending" value={stats.pending} accent="#E65100" />
        <StatCard label="In Progress" value={stats.inProgress} accent="#4527A0" />
        <StatCard label="Resolved" value={stats.resolved} accent="#2E7D32" />
      </Box>

      {/* ── Main panel ──────────────────────────────────────────────────── */}
      <ContentCard sx={{ p: 3,
        display: 'flex',
        flexDirection: 'column',
        gap: 2
      }}>

        {/* Toolbar */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <TextField
            size="small"
            placeholder="Search RMA #, ticket, part, customer…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment:
              <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: 'var(--grey-400)' }} />
                </InputAdornment>

            }}
            sx={{ minWidth: 360, maxWidth: 560, backgroundColor: 'var(--paper)', borderRadius: 1, ...fieldSx }} />
          
          <Box sx={{ flex: 1 }} />
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setCreateOpen(true)}
            sx={{ backgroundColor: '#009688',
              '&:hover': { backgroundColor: '#00796B' },
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.875rem',
              boxShadow: 'none'
            }}>
            
            Create RMA
          </Button>
        </Box>

        <FilterTabs tabs={TABS} value={activeTab} onChange={setActiveTab} />

        {/* Table */}
        <Box sx={{ borderRadius: '8px', border: '1px solid var(--grey-200)', overflow: 'hidden' }}>
          {filtered.length === 0 ?
          <EmptyState
            icon={AssignmentReturnIcon}
            title="No RMAs found"
            description="Try adjusting your search or filter." /> :

          <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={TH}>RMA #</TableCell>
                  <TableCell sx={TH}>Ticket #</TableCell>
                  <TableCell sx={TH}>Part #</TableCell>
                  <TableCell sx={TH}>Description</TableCell>
                  <TableCell sx={TH}>Customer</TableCell>
                  <TableCell sx={TH}>Return Reason</TableCell>
                  <TableCell sx={TH}>Status</TableCell>
                  <TableCell sx={TH}>Disposition</TableCell>
                  <TableCell sx={TH}>Created</TableCell>
                  <TableCell sx={{ ...TH, width: 56 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((rma) =>
              <TableRow
                key={rma.id}
                sx={{ height: 48,
                  cursor: 'pointer',
                  '&:not(:last-child) td': { borderBottom: '1px solid var(--grey-200)' },
                  '&:hover td': { backgroundColor: 'var(--grey-50, #f8fafc)' }
                }}
                onClick={() => navigate(`/inventory/reverse-logistics/${rma.id}`)}>
                
                    <TableCell sx={TD}>
                      <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#009688' }}>
                        {rma.rmaNumber}
                      </Typography>
                    </TableCell>
                    <TableCell sx={TD}>{rma.ticketNumber}</TableCell>
                    <TableCell sx={TD}>{rma.partNumber}</TableCell>
                    <TableCell sx={{ ...TD, maxWidth: 180 }}>
                      <Typography sx={{ fontSize: '0.875rem',
                    color: 'var(--grey-800)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                        {rma.partDescription}
                      </Typography>
                    </TableCell>
                    <TableCell sx={TD}>
                      {(() => {
                    const { short, truncated } = truncate(rma.customerName);
                    return (
                      <Tooltip title={truncated ? rma.customerName : ''} placement="top" arrow enterDelay={300} disableHoverListener={!truncated}>
                            <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-800)' }}>{short}</Typography>
                          </Tooltip>);

                  })()}
                    </TableCell>
                    <TableCell sx={TD}>{rma.returnReason}</TableCell>
                    <TableCell sx={{ ...TD, py: '6px' }}>
                      <StatusBadge status={rma.status} />
                    </TableCell>
                    <TableCell sx={{ ...TD, py: '6px' }}>
                      {rma.disposition ?
                  <StatusBadge status={rma.disposition} /> :

                  <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-400)' }}>—</Typography>
                  }
                    </TableCell>
                    <TableCell sx={TD}>{rma.createdDate}</TableCell>
                    <TableCell
                  sx={{ ...TD, width: 56, py: '6px' }}
                  onClick={(e) => e.stopPropagation()}>
                  
                      <ActionMenu actions={getActions(rma)} />
                    </TableCell>
                  </TableRow>
              )}
              </TableBody>
            </Table>
          }
        </Box>
      </ContentCard>

      {/* ── Modals ──────────────────────────────────────────────────────── */}
      <CreateRMAModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSave={handleCreateRma} />

      <DispositionModal
        open={Boolean(dispositionRma)}
        rma={dispositionRma}
        onClose={() => setDispositionRma(null)}
        onSave={handleSaveDisposition} />

    </Box>);

}